import React, { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react'
import { Box, Flex, Spinner, Text, useToast } from '@chakra-ui/core'
import { FixedSizeGrid, GridChildComponentProps } from 'react-window'
import { Loader } from 'components/CommonComponents/Loader'
import { useInsightsContext } from '../../InsightsStackProvider'
import keys from 'constants/keys'
import useLocaleText from 'components/useLocaleText'
import { SelectDropdown } from 'components/CommonComponents/Select/Control'
import { useSubtasksMediaOverview } from 'components/TasksV2/hooks/useSubtasksMediaOverview'
import { getSubtasksByType } from '../../utils'
import { getInsightsSubtaskTitle } from 'utils/taskAnalytics'
import ExportToExcelButton from 'components/CommonComponents/ExportToExcelButton'
import { exportMediaToExcel } from './utils'
import { useSelector } from 'react-redux'
import { IFileDownload, RootState } from 'constants/interfaces'
import { ControlledCustomSwitch } from 'components/TasksV2/UI/ControlledCustomSwitch'
import { RadioCheckbox } from 'components/TasksV2/UI/RadioCheckbox'
import { MediaOverviewSkeleton } from './MediaOverviewSkeleton'
import { Button } from 'components/ui'
import { downloadZipV2 } from 'utils'
import { Download } from 'lucide-react'
import colors from 'constants/colors'
import { getContextName } from 'hooks/useContextName'
import { getMediaGalleryContexts } from '../../model/mediaGallery'
import { isRtlSelector } from 'redux/selectors/general'

const LazyResultListItem = lazy(() => import('./ResultListItem'))

const GRID_HORIZONTAL_PADDING = 65
const GRID_GAP = 20
const GRID_MIN_COLUMN_WIDTH = 300
const GRID_ROW_HEIGHT = 430
const GRID_OVERSCAN_ROWS = 1

const getGridColumnCount = (width: number, padding: number) => {
  const contentWidth = Math.max(width - padding * 2, 1)

  return Math.max(1, Math.floor((contentWidth + GRID_GAP) / (GRID_MIN_COLUMN_WIDTH + GRID_GAP)))
}

const MediaResultsPreview: React.FC = () => {
  const {
    currentInsightTask,
    deepDiveInsights,
    recurrenceTs,
    insights: allInsights,
    tid,
    selectedSubtaskIds: selectedSubtasksId,
    setSelectedSubtaskIds: setSelectedSubtasksId,
  } = useInsightsContext()

  const { insights, contexts } = deepDiveInsights

  const {
    config: { groups, retailUsersObject },
  } = useSelector((state: RootState) => state.config)
  const theme = useSelector((state: RootState) => state.general.theme)

  const isRtl = useSelector(isRtlSelector)
  const [isChooseStoresMode, setIsChooseStoresMode] = useState(false)
  const [choosedStoresToExport, setChoosedStoresToExport] = useState<string[]>([])
  const [isDownLoading, setIsDownLoading] = useState(false)
  const [gridSize, setGridSize] = useState({ width: 0, height: 0 })

  const gridWrapperRef = useRef<HTMLDivElement | null>(null)

  const toast = useToast()

  const t_no_media = useLocaleText('no_media')
  const task_overview_select_groups_button = useLocaleText('task_overview_select_groups_button')
  const task_overview_select_all_btn = useLocaleText('task_overview_select_all_btn')
  const clear = useLocaleText('clear')

  const { contextIds, subtasks } = getSubtasksByType({
    insights,
    allInsights,
    subtaskTypes: [keys.SUBTASK_TYPES.IMAGE_SUBTASK, keys.SUBTASK_TYPES.VIDEO_SUBTASKS],
    tid,
  })

  const subtaskIds = Array.from(new Set(subtasks.map((subtask) => subtask.st_id)))

  const { displayedContexts, mediaResults, loadMoreData, hasMore, loadingContexts } = useSubtasksMediaOverview(
    currentInsightTask,
    contextIds,
    recurrenceTs,
    subtaskIds
  )

  const contextsWithoutResults = [...Object.keys(contexts).filter((key) => !contextIds.includes(key))]

  const allContexts = useMemo(() => {
    if (hasMore) {
      return displayedContexts
    }

    return Array.from(new Set([...displayedContexts, ...contextsWithoutResults]))
  }, [displayedContexts, contextsWithoutResults, hasMore])

  const reviewContexts = getMediaGalleryContexts([...contextIds, ...contextsWithoutResults], deepDiveInsights, (id) =>
    getContextName(id, groups, retailUsersObject!)
  )

  const handleSelectSubtask = (ids: string[]) => {
    setSelectedSubtasksId(ids)
  }

  const itemsDataArray = useMemo(() => {
    return subtasks.map((subtask) => {
      return {
        id: subtask.st_id,
        name: getInsightsSubtaskTitle(allInsights[tid].sub_tasks[subtask.st_id], subtask.st_id),
      }
    })
  }, [allInsights, subtasks, tid])

  const handleExportToExcel = useCallback(() => {
    let filteredMediaResults = mediaResults
    let filteredSubtasks = subtasks

    if (choosedStoresToExport.length > 0) {
      filteredMediaResults = filteredMediaResults.filter((result) => choosedStoresToExport.includes(result.context_id))
    }

    if (selectedSubtasksId.length > 0) {
      filteredMediaResults = filteredMediaResults.filter((result) => selectedSubtasksId.includes(result.st_id))

      filteredSubtasks = subtasks.filter((subtask) => selectedSubtasksId.includes(subtask.st_id))
    }

    exportMediaToExcel(filteredSubtasks, filteredMediaResults, currentInsightTask.title, groups, retailUsersObject!)
  }, [
    choosedStoresToExport,
    currentInsightTask.title,
    groups,
    mediaResults,
    retailUsersObject,
    selectedSubtasksId,
    subtasks,
  ])

  const handleDownloadZip = useCallback(async () => {
    const filteredMediaResults = choosedStoresToExport
      ? mediaResults.filter(
          (result) =>
            choosedStoresToExport.includes(result.context_id) &&
            (selectedSubtasksId.length === 0 || selectedSubtasksId.includes(result.st_id))
        )
      : mediaResults

    setIsDownLoading(true)

    let generatedFiles: IFileDownload[] = []

    filteredMediaResults.forEach((result) => {
      const context_name = getContextName(result.context_id, groups, retailUsersObject!)
      const subtaskName = subtasks.find((subtask) => subtask.st_id === result.st_id)?.title

      result.images?.forEach((imageURL) => {
        const extension = imageURL.slice(imageURL.lastIndexOf('.'), imageURL.length)
        const newImage = {
          downloadUrl: imageURL,
          name: `${context_name}-${subtaskName}`,
          extension: extension,
        }

        generatedFiles.push(newImage)
      })

      result.videos?.forEach((videoURL) => {
        const extension = videoURL.slice(videoURL.lastIndexOf('.'), videoURL.length)
        const newVideo = {
          downloadUrl: videoURL,
          name: `${context_name}-${subtaskName}`,
          extension: extension,
        }

        generatedFiles.push(newVideo)
      })
    })

    try {
      await downloadZipV2(generatedFiles)
      toast({
        description: 'Download completed successfully!',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        description: 'Error occurred during download!',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }

    setIsDownLoading(false)
  }, [choosedStoresToExport, groups, mediaResults, retailUsersObject, selectedSubtasksId, subtasks, toast])

  const handleChangeMode = useCallback(() => {
    setIsChooseStoresMode((prev) => !prev)
    setChoosedStoresToExport([])
  }, [])

  const handleChooseStores = useCallback(
    (storeId: string) => {
      if (choosedStoresToExport.includes(storeId)) {
        setChoosedStoresToExport((prev) => prev.filter((id) => id !== storeId))
      } else {
        setChoosedStoresToExport((prev) => [...prev, storeId])
      }
    },
    [choosedStoresToExport]
  )

  const handleSelectAll = useCallback(() => {
    if (choosedStoresToExport.length === displayedContexts.length) {
      setChoosedStoresToExport([])
    } else {
      setChoosedStoresToExport((prev) => Array.from(new Set([...prev, ...displayedContexts])))
    }
  }, [choosedStoresToExport.length, displayedContexts])

  const isLoading = isDownLoading
  const hasMediaContexts = contextIds.length > 0
  const isPreparingGrid = hasMediaContexts && displayedContexts.length === 0

  const horizontalPadding = gridSize.width < 700 ? 16 : GRID_HORIZONTAL_PADDING
  const columnCount = getGridColumnCount(gridSize.width, horizontalPadding)
  const contentWidth = Math.max(gridSize.width - horizontalPadding * 2, 1)

  const columnWidth = useMemo(
    () => (contentWidth - GRID_GAP * (columnCount - 1)) / columnCount,
    [columnCount, contentWidth]
  )

  const gridItemCount = allContexts.length + (hasMore ? columnCount : 0)
  const rowCount = Math.ceil(gridItemCount / columnCount)

  const itemData = useMemo(
    () => ({
      allContexts,
      horizontalPadding,
      isRtl,
      reviewContexts,
      choosedStoresToExport,
      columnCount,
      columnWidth,
      displayedContexts,
      handleChooseStores,
      hasMore,
      isChooseStoresMode,
      loadingContexts,
      mediaResults,
      selectedSubtasksId,
      subtasks,
      theme,
    }),
    [
      allContexts,
      horizontalPadding,
      isRtl,
      reviewContexts,
      choosedStoresToExport,
      columnCount,
      columnWidth,
      displayedContexts,
      handleChooseStores,
      hasMore,
      isChooseStoresMode,
      loadingContexts,
      mediaResults,
      selectedSubtasksId,
      subtasks,
      theme,
    ]
  )

  const handleGridItemsRendered = useCallback(
    ({ visibleRowStopIndex, visibleColumnStopIndex }) => {
      if (!hasMore) return

      const visibleStopIndex = visibleRowStopIndex * columnCount + visibleColumnStopIndex

      if (visibleStopIndex >= allContexts.length - columnCount * GRID_OVERSCAN_ROWS) {
        loadMoreData()
      }
    },
    [allContexts.length, columnCount, hasMore, loadMoreData]
  )

  useEffect(() => {
    const node = gridWrapperRef.current

    if (!node) return

    const updateSize = () => {
      setGridSize({ width: node.clientWidth, height: node.clientHeight })
    }

    updateSize()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSize)
      return () => window.removeEventListener('resize', updateSize)
    }

    const observer = new ResizeObserver(updateSize)

    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMediaContexts])

  return (
    <Flex
      flexDir="column"
      h="100%"
      alignItems="center"
      pt="16px"
      pointerEvents={isLoading ? 'none' : 'auto'}
      opacity={!isLoading ? 1 : 0.5}
      overflow="hidden"
    >
      <Flex alignItems="center" justifyContent="center" flexWrap="wrap" px="16px" style={{ gap: '10px' }}>
        {displayedContexts.length > 0 && itemsDataArray.length > 1 && (
          <Box maxW="300px" minW="150px">
            <SelectDropdown
              itemsDataArray={itemsDataArray}
              isLoading={false}
              mode={keys.SELECT_MODES.SINGLE}
              isShowSearch={true}
              selectedIds={selectedSubtasksId}
              onSubmit={handleSelectSubtask}
              menuButtonLiteral="subtasks filter"
              controlButtonProps={{ borderRadius: '10px' }}
              btnH="40px"
            />
          </Box>
        )}
        <Box
          pointerEvents={
            !isChooseStoresMode ? 'auto' : isChooseStoresMode && choosedStoresToExport.length > 0 ? 'auto' : 'none'
          }
          opacity={!isChooseStoresMode ? 1 : isChooseStoresMode && choosedStoresToExport.length > 0 ? 1 : 0.5}
          cursor={
            !isChooseStoresMode
              ? 'pointer'
              : isChooseStoresMode && choosedStoresToExport.length > 0
              ? 'pointer'
              : 'not-allowed'
          }
        >
          <ExportToExcelButton actionWithoutArgs={handleExportToExcel} />
        </Box>

        <Box
          pointerEvents={!isDownLoading && isChooseStoresMode && choosedStoresToExport.length > 0 ? 'auto' : 'none'}
          opacity={isChooseStoresMode && choosedStoresToExport.length > 0 ? 1 : 0.5}
          cursor={isChooseStoresMode && choosedStoresToExport.length > 0 ? 'pointer' : 'not-allowed'}
        >
          <ExportToExcelButton
            icon={
              isDownLoading ? (
                <Spinner size="sm" color={colors.greyMain} />
              ) : (
                <Download size="18px" color={colors.greyMain} />
              )
            }
            actionWithoutArgs={handleDownloadZip}
            literal="download_zip"
          />
        </Box>
      </Flex>

      <Flex
        alignItems="center"
        style={{ columnGap: '10px' }}
        my="20px"
        px="16px"
        flexWrap="wrap"
        justify="center"
        transition="all 0.1s ease-in-out"
      >
        <Flex alignItems="center" lineHeight="normal" style={{ columnGap: '5px' }}>
          <ControlledCustomSwitch isChecked={isChooseStoresMode} onChange={handleChangeMode} />
          <Text>{task_overview_select_groups_button}</Text>
        </Flex>
        {isChooseStoresMode && (
          <>
            <Button type="button" variant="ghost" h="unset" p="5px" fontSize="12px" onClick={handleSelectAll}>
              <Text>
                {choosedStoresToExport.length === displayedContexts.length ? clear : task_overview_select_all_btn}
              </Text>
            </Button>
          </>
        )}
      </Flex>

      {!hasMediaContexts ? (
        <Text fontSize="20px" textAlign="center">
          {t_no_media}
        </Text>
      ) : (
        <Box ref={gridWrapperRef} flex={1} minH="320px" w="100%" alignSelf="stretch">
          {isPreparingGrid || gridSize.width === 0 || gridSize.height === 0 ? (
            <Flex h="100%" minH="320px" align="center" justify="center">
              <Loader />
            </Flex>
          ) : (
            <FixedSizeGrid
              direction={isRtl ? 'rtl' : 'ltr'}
              columnCount={columnCount}
              columnWidth={columnWidth + GRID_GAP}
              height={gridSize.height}
              rowCount={rowCount}
              rowHeight={GRID_ROW_HEIGHT}
              width={gridSize.width}
              itemData={itemData}
              overscanRowCount={GRID_OVERSCAN_ROWS}
              onItemsRendered={handleGridItemsRendered}
            >
              {MediaResultGridCell}
            </FixedSizeGrid>
          )}
        </Box>
      )}
    </Flex>
  )
}

const MediaResultGridCell: React.FC<GridChildComponentProps> = ({ columnIndex, rowIndex, style, data }) => {
  const {
    allContexts,
    horizontalPadding,
    isRtl,
    reviewContexts,
    choosedStoresToExport,
    columnCount,
    columnWidth,
    displayedContexts,
    handleChooseStores,
    hasMore,
    isChooseStoresMode,
    loadingContexts,
    mediaResults,
    selectedSubtasksId,
    subtasks,
    theme,
  } = data

  const itemIndex = rowIndex * columnCount + columnIndex
  const contextId = allContexts[itemIndex]

  const cellStyle = {
    ...style,
    [isRtl ? 'right' : 'left']: Number((isRtl ? style.right : style.left) || 0) + horizontalPadding,
    top: Number(style.top || 0) + GRID_GAP / 2,
    width: columnWidth,
    height: GRID_ROW_HEIGHT - GRID_GAP,
  }

  if (!contextId) {
    return (
      <Flex style={cellStyle} align="center" justify="center">
        {hasMore && itemIndex === allContexts.length ? <Loader /> : null}
      </Flex>
    )
  }

  const results =
    mediaResults?.filter((res: RootState['tasks_v2']['allResults'][string][number]) => {
      const isContextMatch = res.context_id === contextId
      const isSubtaskMatch = selectedSubtasksId.length === 0 || selectedSubtasksId.includes(res.st_id)

      return isContextMatch && isSubtaskMatch
    }) ?? []

  const isLoading = loadingContexts[contextId] ?? false

  return (
    <Flex
      style={cellStyle}
      maxW={{ base: '100%', md: displayedContexts.length === 1 ? '350px' : 'unset' }}
      alignItems="center"
      justifyContent="space-between"
    >
      {isChooseStoresMode && (
        <>
          {results.length > 0 ? (
            <RadioCheckbox
              value={contextId}
              isChecked={choosedStoresToExport.includes(contextId)}
              onChange={() => handleChooseStores(contextId)}
              fillColor={theme?.elementsColor}
            />
          ) : (
            <Box>
              <Box h="20px" w="20px" />
            </Box>
          )}
        </>
      )}

      <Box
        transition="all 0.1s ease-in-out"
        transform={isChooseStoresMode ? 'scale(0.9)' : 'scale(1)'}
        w="100%"
        pointerEvents={isChooseStoresMode ? 'none' : 'auto'}
        opacity={!isChooseStoresMode ? 1 : 0.5}
      >
        <Suspense fallback={<MediaOverviewSkeleton />}>
          <LazyResultListItem
            key={`${contextId}:${selectedSubtasksId.join('|')}`}
            reviewContexts={reviewContexts}
            contextId={contextId}
            results={results}
            isLoading={isLoading}
            subtasks={subtasks}
            selectedSubtaskIds={selectedSubtasksId}
          />
        </Suspense>
      </Box>
    </Flex>
  )
}

export default MediaResultsPreview
